import React, { useCallback, useMemo, useEffect } from 'react';
import { ReactFlow, Controls, useReactFlow, useViewport } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { LocationMarkerNode } from './nodes/LocationMarkerNode';
import { LocationEdge } from './edges/LocationEdge';
import { SheetBackground } from './SheetBackground';
import { ElementZoomControl } from './ElementZoomControl';
import { LocationBackgroundControl } from './LocationBackgroundControl';
import { useStore, getActivePage } from '@/state/store';
import { SHEET, INNER } from '@/lib/svgExport';
import { computeLocationModel, markerLabel } from '@/lib/location';
import { useI18n } from '@/i18n/I18nContext';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { MapPin, Cable } from 'lucide-react';

const nodeTypes = { locationMarker: LocationMarkerNode };
const edgeTypes = { location: LocationEdge };

// Background image layer: rendered behind the React Flow markers/edges but
// inside the same viewport transform so it pans/zooms in lock-step. The CAD
// frame + title block are drawn by SheetBackground (behind) and an overlay
// (in front) so the title block stays visible over the background.
const LocationBgLayer = () => {
  const { x, y, zoom } = useViewport();
  const page = useStore(getActivePage);
  const dataUri = page?.background?.dataUri;
  if (!dataUri) return null;
  const innerW = SHEET.w - 2 * INNER;
  const innerH = SHEET.h - 2 * INNER;
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true">
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: SHEET.w,
          height: SHEET.h,
          transform: `translate(${x}px, ${y}px) scale(${zoom})`,
          transformOrigin: '0 0',
        }}
      >
        <img
          src={dataUri}
          alt=""
          style={{ position: 'absolute', left: INNER, top: INNER, width: innerW, height: innerH, objectFit: 'contain' }}
          data-testid="location-bg-image"
        />
      </div>
    </div>
  );
};

export const LocationCanvas = () => {
  const { t, language } = useI18n();
  const doc = useStore((s) => s.doc);
  const page = useStore(getActivePage);
  const selectedNodeId = useStore((s) => s.selectedNodeId);
  const selectNode = useStore((s) => s.selectNode);
  const selectEdge = useStore((s) => s.selectEdge);
  const selectedEdgeId = useStore((s) => s.selectedEdgeId);
  const updateLocationMarkerPos = useStore((s) => s.updateLocationMarkerPos);
  const setLocationShowConnections = useStore((s) => s.setLocationShowConnections);
  const snapEnabled = useStore((s) => s.snapEnabled);
  const { fitBounds } = useReactFlow();

  const fitSheet = useCallback(() => {
    // Location pages fit the FULL sheet regardless of element zoom: the element
    // zoom must scale only the markers, never the background/frame/navigation.
    fitBounds({ x: 0, y: 0, width: SHEET.w, height: SHEET.h }, { padding: 0.04, duration: 200 });
  }, [fitBounds]);

  useEffect(() => {
    if (!page?.id) return undefined;
    const raf = requestAnimationFrame(fitSheet);
    return () => cancelAnimationFrame(raf);
  }, [page?.id, fitSheet]);

  const model = useMemo(() => (page ? computeLocationModel(doc, page) : { markers: [], edges: [] }), [doc, page]);
  const memberIds = useMemo(() => new Set(model.markers.map((m) => m.deviceId)), [model]);
  const showConnections = page?.showConnections !== false;

  const markerScale = page?.contentScale || 1;
  const rfNodes = useMemo(
    () =>
      model.markers.map((m) => ({
        id: m.deviceId,
        type: 'locationMarker',
        position: m.position,
        selected: m.deviceId === selectedNodeId,
        data: { deviceId: m.deviceId, deviceType: m.node.data?.deviceType, label: markerLabel(m.node, language), scale: markerScale },
      })),
    [model, selectedNodeId, language, markerScale]
  );

  const rfEdges = useMemo(() => {
    if (!showConnections) return [];
    return model.edges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      sourceHandle: 'c',
      targetHandle: 'c',
      type: 'location',
      selected: e.id === selectedEdgeId,
      data: { connType: e.connType },
    }));
  }, [model, showConnections, selectedEdgeId]);

  const onNodesChange = useCallback(
    (changes) => {
      if (!page) return;
      changes.forEach((c) => {
        if (c.type === 'position' && c.position && memberIds.has(c.id)) {
          updateLocationMarkerPos(page.id, c.id, c.position);
        }
      });
    },
    [page, memberIds, updateLocationMarkerPos]
  );

  if (!page) return null;

  return (
    <div className="flex h-full w-full flex-col" data-testid="location-canvas">
      {/* Location toolbar */}
      <div className="flex flex-wrap items-center gap-3 border-b border-border bg-[#FBFBFA] px-3 py-2">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <MapPin className="h-3.5 w-3.5" />
          <span className="font-semibold uppercase tracking-wide">{t('location_plan')}</span>
          <span className="tabular-nums">· {t('location_members_count', { n: model.markers.length })}</span>
        </div>

        <div className="ml-auto flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Cable className="h-3.5 w-3.5 text-muted-foreground" />
            <Switch
              id="location-show-conns"
              checked={showConnections}
              onCheckedChange={(v) => setLocationShowConnections(page.id, v)}
              data-testid="location-show-connections-switch"
            />
            <Label htmlFor="location-show-conns" className="cursor-pointer text-xs text-[#2A3138]">
              {t('show_connections')}
            </Label>
          </div>

          <LocationBackgroundControl />

          <div className="h-5 w-px bg-border" />

          <ElementZoomControl page={page} />
        </div>
      </div>

      {/* Canvas */}
      <div className="netdraw-canvas relative min-h-0 flex-1 bg-[#e9e9e4]">
        <SheetBackground />
        <LocationBgLayer />
        <ReactFlow
          nodes={rfNodes}
          edges={rfEdges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodesChange={onNodesChange}
          onNodeClick={(_, node) => memberIds.has(node.id) && selectNode(node.id)}
          onEdgeClick={(_, edge) => selectEdge(edge.id)}
          onPaneClick={() => selectNode(null)}
          nodesConnectable={false}
          edgesFocusable={true}
          snapToGrid={snapEnabled}
          snapGrid={[16, 16]}
          minZoom={0.1}
          maxZoom={3}
          deleteKeyCode={null}
          onInit={() => requestAnimationFrame(fitSheet)}
          proOptions={{ hideAttribution: true }}
        >
          <Controls showInteractive={false} />
        </ReactFlow>
        <SheetBackground overlay />

        {model.markers.length === 0 && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center px-8 text-center">
            <p className="z-10 max-w-md rounded-md bg-white/85 px-4 py-3 text-sm text-muted-foreground ring-1 ring-border" data-testid="location-empty-hint">
              {t('location_empty')}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
